import { useNavigate } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import {
  FileUploaderSettingsDocument,
  FileUploaderSettingsQuery,
} from 'generated/resources/graphql'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import filesStore from 'modules/documents/components/Documents/store'
import UploadFormWizard from 'modules/documents/components/UploadFormWizard'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { SPACES } from 'modules/documents/lib/constants'
import type Employee from 'modules/employees/store/models/Employee'

const toFolders = (
  editableFolders: FileUploaderSettingsQuery['documents']['editableEmployeeFolders'] | undefined
): Folder[] => {
  if (!editableFolders) {
    return []
  }
  // DocumentsEditableEmployeeFolder and DocumentsFolder have identical field structures
  // except for __typename fields. We map to ensure type compatibility with Folder type.
  return editableFolders.map((folder) => {
    const { parentFolder, rootParentFolder, _policies, ...rest } = folder
    return {
      ...rest,
      __typename: 'DocumentsFolder' as const,
      parentFolder: parentFolder ? { id: parentFolder.id } : null,
      rootParentFolder: rootParentFolder ? { id: rootParentFolder.id } : null,
      _policies: _policies
        ? {
            ..._policies,
            __typename: 'DocumentsFolderPolicies' as const,
          }
        : null,
    } satisfies Folder
  })
}

interface Props {
  employee: Employee
  folder?: Folder
  backRoute: string
}

const NewDocument = ({ employee, folder, backRoute }: Props) => {
  const navigate = useNavigate()
  const {
    company: { subscription },
  } = useCurrentSession()
  const { data, loading } = useQuery(FileUploaderSettingsDocument, {
    variables: {
      employeeId: employee.id,
      space: SPACES.EMPLOYEE_MY_DOCUMENTS,
    },
  })

  const editableEmployeeFoldersRaw = toFolders(data?.documents.editableEmployeeFolders)
  const targetEmployee = data?.employees.employeesConnection.nodes[0]
  const legalEntity = data?.companies.legalEntitiesConnection.nodes[0]
  const canUseCompanyDocuments = data?.documents._policies?.companyUseCompanyDocuments.can

  const onClose = () => {
    filesStore.setFiles([])
    navigate(backRoute)
  }

  if (loading)
    return (
      <Box padding="s32" width="full" alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <UploadFormWizard
        space={SPACES.EMPLOYEE_MY_DOCUMENTS}
        legalEntity={legalEntity}
        targetEmployee={targetEmployee}
        folders={editableEmployeeFoldersRaw}
        selectedFolder={folder}
        onClose={onClose}
        canUseCompanyDocuments={canUseCompanyDocuments}
      />
    </ExpiredTrialChecker>
  )
}

export default NewDocument
